
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ProfileData } from '@/hooks/useProfiles';
import { Card, CardContent } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
interface ProfileInfoProps {
  profile: ProfileData;
  hideTitle?: boolean;
}
const ProfileInfo = ({
  profile,
  hideTitle = false
}: ProfileInfoProps) => {
  const isMobile = useIsMobile();

  // Function to format location with proper capitalization
  const formatLocation = (location: string | null): string => {
    if (!location) return '';
    // Replace dashes with spaces first, then format each word
    return location
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get education level display name
  const getEducationDisplayName = (educationCode: string | null): string => {
    if (!educationCode) return '';
    const educationMap: Record<string, string> = {
      'high_school': 'High school diploma or equivalent',
      'bachelors': 'Bachelor\'s degree',
      'mba': 'MBA International Business Management',
      'masters': 'Master\'s degree',
      'doctorate': 'Doctorate (Ph.D., Ed.D., etc.)'
    };
    return educationMap[educationCode] || educationCode;
  };

  // Get experience level display name
  const getExperienceLevelDisplayName = (years: number | null): string => {
    if (years === null || years === undefined) return '';
    if (years < 1) return 'Less than 1 year';
    if (years <= 2) return '1-2 years';
    if (years <= 5) return '3-5 years';
    if (years <= 10) return '6-10 years';
    if (years <= 15) return '11-15 years';
    if (years <= 20) return '16-20 years';
    return 'More than 20 years';
  };
  return <div className="space-y-6">
      {/* Responsive grid - asymmetric layout with dominant bio card on desktop, single column on mobile */}
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-template-columns: 2fr 1fr'} gap-4`} style={!isMobile ? {
      gridTemplateColumns: '65% 32%'
    } : undefined}>
        {/* Bio Card - Dominant width on desktop */}
        <Card className="h-full">
          <CardContent className={`p-4 sm:p-6`}>
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-4">Bio</h3>
            {profile.bio ? <p className="text-muted-foreground whitespace-pre-line leading-relaxed text-xl">{profile.bio}</p> : <p className="text-muted-foreground italic">No bio provided</p>}
          </CardContent>
        </Card>
        
        {/* Combined Skills, Experience, Education and Languages Card - Narrower width */}
        <Card className="h-full">
          <CardContent className={`p-4 sm:p-6 flex flex-col h-full`}>
            {/* Skills Section */}
            {profile.skills && profile.skills.length > 0 && <div className="mb-4 sm:mb-5">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, index) => <Badge key={index} variant="outline" className="px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm rounded-full bg-white">
                      {skill}
                    </Badge>)}
                </div>
              </div>}
            
            {/* Experience level information */}
            {profile.years_experience !== null && profile.years_experience !== undefined && <div className="mb-4 sm:mb-5 border-t border-gray-100 pt-3 sm:pt-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-3xl sm:text-4xl font-bold">
                    <span className="text-yellow-500">+</span>
                    <span className="text-neutral-800">{profile.years_experience}</span>
                  </span>
                </div>
                <p className="text-sm sm:text-base font-medium text-neutral-600 mt-0.5 sm:mt-1">Years of Experience</p>
              </div>}
            
            {/* Education Section */}
            {profile.education && <div className="mb-4 sm:mb-5 border-t border-gray-100 pt-3 sm:pt-4">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Education</h3>
                <p className="text-muted-foreground leading-relaxed">{getEducationDisplayName(profile.education)}</p>
              </div>}

            {/* Languages Section - Removed mt-auto to bring it closer to Education */}
            {profile.languages && profile.languages.length > 0 && <div className="border-t border-gray-100 pt-3 sm:pt-4">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((language, index) => <Badge key={index} variant="secondary" className="px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm rounded-full">
                      {language}
                    </Badge>)}
                </div>
              </div>}

            {/* Empty state if no skills, education, experience or languages */}
            {!profile.skills?.length && !profile.education && !profile.languages?.length && profile.years_experience === null && <p className="text-muted-foreground italic">No additional information provided</p>}
            
            {/* Spacer div to maintain card height */}
            <div className="flex-grow"></div>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default ProfileInfo;
